import React, { useEffect, useState, useCallback, useRef } from 'react';
import { HlsVideo } from './HlsVideo';
import { DeviceStatusIcon, ProviderPill } from '../access/DeviceVisuals';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
function authHeaders(): HeadersInit {
  const t = localStorage.getItem('intrlock_token') || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Grid of live camera tiles. Each Turing camera fetches its own stream URL
// via the connector-api /stream-url route (backend does the KMS unwrap +
// HLS proxy rewrite; SPA just receives an authenticated proxy URL).
// Non-Turing cameras render a placeholder tile.
export const CameraGridView: React.FC<{
  cameras: any[];
  turingSystems: any[];
  unifiSystems?: any[];
  onTileClick?: (c: any) => void;
}> = ({ cameras, turingSystems, unifiSystems = [], onTileClick }) => {
  if (!cameras.length) {
    return <div className="p-8 text-center text-gray-400">No cameras.</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {cameras.map((c, idx) => (
        <CameraTile key={c.id} camera={c} turingSystems={turingSystems} unifiSystems={unifiSystems} onTileClick={onTileClick} tileIndex={idx} />
      ))}
    </div>
  );
};

// Only load a tile when it scrolls into view (IntersectionObserver) AND stagger
// the initial batch by tileIndex to avoid firing 26 parallel stream-url calls.
const CameraTile: React.FC<{ camera: any; turingSystems: any[]; unifiSystems: any[]; onTileClick?: (c: any) => void; tileIndex: number }> = ({ camera, turingSystems, unifiSystems, onTileClick, tileIndex }) => {
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [systemId, setSystemId] = useState<string>('');
  const [visible, setVisible] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!rootRef.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) { setVisible(true); io.disconnect(); }
    }, { rootMargin: '200px' });
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, []);

  const load = useCallback(async () => {
    const isTuring = camera.acs_provider === 'turing';
    const isUnifi = camera.acs_provider === 'unifi_protect';
    if (!isTuring && !isUnifi) {
      setError('Preview not supported for this camera type');
      return null;
    }
    const systems = isTuring ? turingSystems : unifiSystems;
    const system = systems.find((s) => s.entity_type === 'property' && String(s.entity_id) === String(camera.property_id))
      || systems.find((s) => s.entity_type === 'customer' && String(s.entity_id) === String(camera.customer_id));
    if (!system) { setError(`No ${isTuring ? 'Turing' : 'UniFi Protect'} system attached to this property.`); return null; }
    setSystemId(system.id);
    const cameraId = camera.envelope_token || camera.acs_device_id;
    if (!cameraId) { setError('Camera missing envelope token.'); return null; }
    try {
      const route = isTuring ? 'turing' : 'unifi-protect';
      const r = await fetch(`${API_BASE}/integrations/${route}/systems/${system.id}/stream-url`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(isTuring ? { camera_id: cameraId, resolution: 'sub' } : { camera_id: cameraId, quality: 'low' }),
      });
      const data = await r.json();
      if (data.error === 'license_required') { setError('No license.'); return null; }
      if (data.play_url) { setStreamUrl(data.play_url); setError(null); return data.play_url; }
      setError('No stream URL.');
      return null;
    } catch (e: any) { setError(String(e?.message ?? e).slice(0, 60)); return null; }
  }, [camera, turingSystems, unifiSystems]);

  useEffect(() => {
    if (!visible) return;
    const delay = Math.floor(tileIndex / 4) * 250;
    const t = setTimeout(() => { void load(); }, delay);
    return () => clearTimeout(t);
  }, [visible, load, tileIndex]);

  const refresh = useCallback(async () => {
    if (!systemId) return null;
    try {
      const cameraId = camera.envelope_token || camera.acs_device_id;
      const isTuring = camera.acs_provider === 'turing';
      const route = isTuring ? 'turing' : 'unifi-protect';
      const r = await fetch(`${API_BASE}/integrations/${route}/systems/${systemId}/stream-url`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(isTuring ? { camera_id: cameraId, resolution: 'sub' } : { camera_id: cameraId, quality: 'low' }),
      });
      const data = await r.json();
      return data.play_url || null;
    } catch { return null; }
  }, [camera, systemId]);

  return (
    <div
      ref={rootRef}
      className="relative aspect-video bg-black rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-brand-500 transition-all"
      onClick={() => onTileClick?.(camera)}
      title={camera.name || 'Camera'}
    >
      {error ? (
        <div className="absolute inset-0 flex items-center justify-center text-white/70 text-xs p-3 text-center">{error}</div>
      ) : streamUrl ? (
        <HlsVideo src={streamUrl} onRefreshStreamUrl={refresh} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/50 text-xs">Loading…</div>
      )}
      <div className="absolute top-2 right-2"><DeviceStatusIcon online={!error && !!streamUrl} label={error ? 'Offline' : streamUrl ? 'Streaming' : 'Connecting'} /></div>
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5 pointer-events-none">
        <div className="flex items-center gap-2"><ProviderPill provider={camera.acs_provider || (camera._source === 'bridge' ? 'bridge' : camera.manufacturer || '')}/><p className="text-white text-xs font-semibold truncate">{camera.name || camera.device_name || 'Camera'}</p></div>
        {camera.property_name && <p className="text-white/60 text-[10px] truncate">{camera.property_name}</p>}
      </div>
    </div>
  );
};
